import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AgencyQuoteView() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/public/agency-tx/quote/${token}`); setQuote(r.data); }
    catch { setError('This quote could not be found.'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const respond = async (action, reason) => {
    setBusy(true);
    try { await axios.post(`${API}/public/agency-tx/quote/${token}/${action}`, reason ? { reason } : {}); await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <Center><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></Center>;
  if (error && !quote) return <Center><div className="text-slate-500">{error}</div></Center>;

  const decided = ['accepted', 'declined', 'converted'].includes(quote.status);
  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="public-quote-view">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-teal-700 text-white px-6 py-5">
          <div className="text-sm opacity-80">{quote.agency_name}</div>
          <div className="flex items-center gap-2 mt-1"><FileText className="w-5 h-5" /><h1 className="text-xl font-bold">Quote {quote.quote_number}</h1></div>
          {quote.title && <div className="opacity-90 mt-1">{quote.title}</div>}
        </div>
        <div className="p-6">
          <table className="w-full text-sm mb-4">
            <thead><tr className="text-left text-slate-400 border-b"><th className="py-2">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit</th><th className="py-2 text-right">Amount</th></tr></thead>
            <tbody>{(quote.items || []).map((it) => (
              <tr key={it.id} className="border-b"><td className="py-2">{it.description}</td><td className="py-2 text-right">{it.quantity}</td><td className="py-2 text-right">{money(it.unit_price_cents)}</td><td className="py-2 text-right">{money(it.amount_cents)}</td></tr>
            ))}</tbody>
          </table>
          <div className="space-y-1 text-sm">
            <Row label="Subtotal" val={money(quote.subtotal_cents)} />
            {quote.discount_cents > 0 && <Row label="Discount" val={`−${money(quote.discount_cents)}`} />}
            {quote.tax_cents > 0 && <Row label={`Tax (${quote.tax_pct}%)`} val={money(quote.tax_cents)} />}
            <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span>{money(quote.total_cents)}</span></div>
          </div>
          {quote.terms && <div className="mt-4 text-sm text-slate-600"><div className="font-medium">Terms</div>{quote.terms}</div>}

          <div className="mt-6">
            {quote.status === 'accepted' && <Banner ok icon={<CheckCircle2 className="w-5 h-5" />}>You accepted this quote. {quote.agency_name} will follow up with next steps.</Banner>}
            {quote.status === 'converted' && <Banner ok icon={<CheckCircle2 className="w-5 h-5" />}>This quote has been accepted and is being processed.</Banner>}
            {quote.status === 'declined' && <Banner icon={<XCircle className="w-5 h-5" />}>You declined this quote.</Banner>}
            {!decided && (
              <div className="flex gap-3">
                <Button className="flex-1 bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => respond('accept')} data-testid="accept-quote-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept quote'}</Button>
                <Button variant="outline" className="flex-1" disabled={busy} onClick={() => respond('decline', window.prompt('Reason (optional):') || '')} data-testid="decline-quote-btn">Decline</Button>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-3 bg-slate-50 text-center text-xs text-slate-400">Secure documents powered by iEvntür</div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-100">{children}</div>;
const Row = ({ label, val }) => <div className="flex justify-between"><span className="text-slate-500">{label}</span><span>{val}</span></div>;
const Banner = ({ children, ok, icon }) => (
  <div className={`flex items-center gap-2 rounded-lg p-3 text-sm ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{icon}{children}</div>
);
